/**
 * Ask AI Coach transcript helpers (export/copy). Per-game chats are also stored server-side.
 */

function pad2(n) {
  return String(n).padStart(2, '0');
}

/** Local date as yyyy-mm-dd */
export function formatCoachExportDate(d = new Date()) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** Local datetime stamp for filenames: yyyy-mm-dd-HHmm */
export function formatCoachExportStamp(d = new Date()) {
  return `${formatCoachExportDate(d)}-${pad2(d.getHours())}${pad2(d.getMinutes())}`;
}

/**
 * @param {Array<{ role?: string, content?: string, typing?: boolean }>} messages
 * @param {{ gameId?: number|string|null, focusPly?: number|null, now?: Date }} meta
 */
export function formatCoachChatMarkdown(messages, meta = {}) {
  const now = meta.now instanceof Date ? meta.now : new Date();
  const lines = [
    '# Ask AI Coach',
    '',
    `Date: ${now.toLocaleString()}`,
  ];
  if (meta.gameId != null && meta.gameId !== '') {
    lines.push(`Game: #${meta.gameId}`);
  }
  if (meta.focusPly != null && Number.isFinite(Number(meta.focusPly))) {
    lines.push(`Focus ply: ${Number(meta.focusPly)}`);
  }
  lines.push('');

  for (const m of messages || []) {
    if (!m || (m.role !== 'user' && m.role !== 'assistant')) continue;
    const content = String(m.content || '').trim();
    if (!content && !m.verifiedAnalysis) continue;
    const heading = m.role === 'user' ? 'You' : 'Ask AI Coach';
    lines.push(`## ${heading}`, '');
    if (content) lines.push(content, '');
  }

  return `${lines.join('\n').trimEnd()}\n`;
}

export function coachChatExportFilename({ gameId, now = new Date() } = {}) {
  if (gameId != null && gameId !== '') {
    return `ask-ai-coach-game-${gameId}-${formatCoachExportDate(now)}.md`;
  }
  return `ask-ai-coach-${formatCoachExportStamp(now)}.md`;
}

export function downloadTextFile(filename, text) {
  const blob = new Blob([text], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** @returns {Promise<boolean>} true if clipboard write succeeded */
export async function copyText(text) {
  const value = String(text || '');
  if (!value) return false;
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return true;
  }
  // Fallback for older browsers / insecure contexts
  const ta = document.createElement('textarea');
  ta.value = value;
  ta.setAttribute('readonly', '');
  ta.style.position = 'fixed';
  ta.style.left = '-9999px';
  document.body.appendChild(ta);
  ta.select();
  const ok = document.execCommand('copy');
  ta.remove();
  return ok;
}
